import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BrainCircuit, TrendingUp, LineChart, Shield, Zap, Award, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { subscriptionService, SUBSCRIPTION_PLANS, SUBSCRIPTION_STATUS } from '../services/api';


// Initialize Stripe with your publishable key
// Initialize Stripe
// Initialize Stripe
const getStripe = () => {
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  
  if (!stripeKey) {
    console.error('Stripe publishable key is missing! Please check your environment variables.');
    return null;
  }
  
  if (!window.stripePromise) {
    window.stripePromise = loadStripe(stripeKey);
  }
  return window.stripePromise;
};

const SubscriptionPlans = () => {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Define plans array inside the component
  const plans = [
    {
      name: 'Basic',
      id: 'basic_tier',
      stripeMonthlyPriceId: 'price_monthly_basic_id',
      stripeAnnualPriceId: 'price_annual_basic_id',
      icon: Shield,
      price: { monthly: 19, annually: 190 },
      description: 'Enhanced tools for serious investors',
      features: [
        'Basic financial calculators',
        'Market dicussions newsletter',
        'Standard market analytics'
      ],
      color: 'border-blue-500'
    },
    {
      name: 'Premium',
      id: 'premium_tier',
      stripeMonthlyPriceId: 'price_monthly_premium_id',
      stripeAnnualPriceId: 'price_annual_premium_id',
      icon: Award,
      popular: true,
      price: { monthly: 49, annually: 490 },
      description: 'Professional-grade AI financial analysis',
      features: [
        'All Basic features',
        'Advanced financial calculators',
        'Investment/market trend discussions',
        'Real-time portfolio insights',
        'Enhanced market analytics'
      ],
      color: 'border-purple-500'
    },
    {
      name: 'Elite',
      id: 'elite_tier',
      stripeMonthlyPriceId: 'price_monthly_elite_id',
      stripeAnnualPriceId: 'price_annual_elite_id',
      icon: Crown,
      price: { monthly: 99, annually: 990 },
      description: 'Ultimate AI-powered financial mastery',
      features: [
        'All Premium features',
        'Personalized AI financial advisor',
        'Advanced risk analytics',
        'Custom investment strategies',
        'Horizon plus forum',
        'Early access to new features'
      ],
      color: 'border-amber-500'
    }
  ];

  const handleSubscribe = async (plan) => {
    if (!user) {
      // Redirect to login if user is not authenticated
      window.location.href = '/login';
      return;
    }

    try {
      setLoading(true);

      if (plan.name === 'Free') {
        await subscriptionService.subscribeFreetier(user.userId, plan.id);
        window.location.href = '/dashboard';
        return;
      }

      // Get Stripe instance
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Failed to initialize Stripe. Please check your configuration.');
      }

      // Create checkout session using subscription service
      const { sessionId } = await subscriptionService.createCheckoutSession(
        billingInterval === 'monthly' ? plan.stripeMonthlyPriceId : plan.stripeAnnualPriceId,
        user.userId,
        user.email,
        plan.id,
        billingInterval
      );

      // Redirect to Stripe checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('Error handling subscription:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            Choose Your Financial Journey
          </h2>
          <p className="mt-4 text-xl text-gray-600">
            Transform your investment decisions with AI-powered insights
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="mt-12 flex justify-center">
          <div className="relative bg-white rounded-lg p-1 flex">
            <button
              className={`${
                billingInterval === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500'
              } px-4 py-2 rounded-md transition-all duration-200`}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </button>
            <button
              className={`${
                billingInterval === 'annually'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500'
              } px-4 py-2 rounded-md transition-all duration-200`}
              onClick={() => setBillingInterval('annually')}
            >
              Annually
              <span className="ml-1 text-xs font-semibold">
                (Save 20%)
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="mt-12 grid gap-8 lg:gap-12 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative flex flex-col border-2 ${plan.color} ${
                plan.popular ? 'scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-5 left-0 right-0 mx-auto w-32 rounded-full bg-purple-500 text-white text-sm text-center py-1 shadow-lg">
                  Most Popular
                </div>
              )}
              
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-bold">{plan.name}</CardTitle>
                  <plan.icon className={`h-6 w-6 ${
                    plan.popular ? 'text-purple-500' : 'text-blue-500'
                  }`} />
                </div>
              </CardHeader>

              <CardContent className="flex-grow">
                <div className="mt-4">
                  <span className="text-3xl font-bold">
                    ${plan.price[billingInterval]}
                  </span>
                  {plan.price[billingInterval] > 0 && (
                    <span className="text-gray-500">
                      /{billingInterval === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  )}
                </div>

                <p className="mt-4 text-gray-600">{plan.description}</p>

                <ul className="mt-6 space-y-4">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <TrendingUp className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan)}
                  disabled={loading}
                  className={`mt-8 w-full py-3 px-4 rounded-md text-white font-medium transition-colors ${
                    loading ? 'bg-gray-400 cursor-not-allowed' :
                    plan.popular
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Processing...' : plan.name === 'Free' ? 'Get Started' : 'Subscribe Now'}
                </button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-3">
          <div className="text-center">
            <BrainCircuit className="mx-auto h-12 w-12 text-blue-500" />
            <h3 className="mt-4 text-lg font-medium">AI-Powered Analysis</h3>
            <p className="mt-2 text-gray-600">
              Advanced algorithms provide personalized financial insights
            </p>
          </div>
          <div className="text-center">
            <Shield className="mx-auto h-12 w-12 text-blue-500" />
            <h3 className="mt-4 text-lg font-medium">Secure & Reliable</h3>
            <p className="mt-2 text-gray-600">
              Enterprise-grade security protecting your financial data
            </p>
          </div>
          <div className="text-center">
            <LineChart className="mx-auto h-12 w-12 text-blue-500" />
            <h3 className="mt-4 text-lg font-medium">Real-time Updates</h3>
            <p className="mt-2 text-gray-600">
              Stay informed with live market data and analytics
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;